/**
 * ZoomSlider - A slider component for seamless zoom control in React Flow
 */

import { useCallback } from 'react'
import { useReactFlow, useViewport } from '@xyflow/react'
import styled from 'styled-components'
import { Minus, Plus } from 'lucide-react'
import {
  COLORS,
  BORDER_RADIUS,
  SPACING,
  TRANSITIONS,
  TYPOGRAPHY,
  FONT_WEIGHT,
} from '../../../styles/theme'
import { COLLAPSE_ZOOM_THRESHOLD } from '../utils/zoomUtils'

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const SliderContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${SPACING.sm};
  background: var(--color-bg-elevated);
  padding: 0 ${SPACING.md};
  height: 40px;
  border-radius: ${BORDER_RADIUS.lg};
  box-shadow: var(--shadow-dropdown);
  border: 1px solid var(--color-border-dark);
`

const ZoomButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: ${BORDER_RADIUS.md};
  border: none;
  background: transparent;
  color: var(--color-text-secondary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${TRANSITIONS.fast};

  &:hover {
    color: var(--color-text-primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const SliderTrack = styled.div`
  position: relative;
  width: 100px;
  height: 4px;
  background: var(--color-border-dark);
  border-radius: ${BORDER_RADIUS.full};
`

const ThresholdMarker = styled.div.attrs<{ $percentage: number }>((props) => ({
  style: { left: `${props.$percentage}%` },
}))`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 2px;
  height: 12px;
  background: var(--color-text-tertiary);
  border-radius: 1px;
  pointer-events: none;
`

const SliderFill = styled.div.attrs<{ $percentage: number }>((props) => ({
  style: { width: `${props.$percentage}%` },
}))`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: ${COLORS.primary};
  border-radius: ${BORDER_RADIUS.full};
  transition: width 0.1s ease-out;
`

const SliderInput = styled.input`
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  width: 100%;
  height: 20px;
  margin: 0;
  opacity: 0;
  cursor: pointer;
  z-index: 1;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
  }
`

const SliderThumb = styled.div.attrs<{ $percentage: number }>((props) => ({
  style: { left: `${props.$percentage}%` },
}))`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background: var(--color-bg-elevated);
  border: 2px solid ${COLORS.primary};
  border-radius: ${BORDER_RADIUS.full};
  box-shadow: var(--shadow-card);
  pointer-events: none;
  transition:
    transform 0.1s ease-out,
    left 0.1s ease-out;

  ${SliderInput}:hover + & {
    transform: translate(-50%, -50%) scale(1.1);
  }

  ${SliderInput}:active + & {
    transform: translate(-50%, -50%) scale(0.95);
  }
`

const ZoomLabel = styled.span`
  min-width: 40px;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.medium};
  color: var(--color-text-secondary);
  text-align: center;
`

// ============================================================================
// COMPONENT
// ============================================================================

interface ZoomSliderProps {
  minZoom?: number
  maxZoom?: number
  step?: number
}

export function ZoomSlider({ minZoom = 0.1, maxZoom = 1.5, step = 0.1 }: ZoomSliderProps) {
  const { zoomTo } = useReactFlow()
  const { zoom } = useViewport()

  // Calculate percentage for slider position
  const percentage = ((zoom - minZoom) / (maxZoom - minZoom)) * 100

  // Calculate threshold marker position
  const thresholdPercentage = ((COLLAPSE_ZOOM_THRESHOLD - minZoom) / (maxZoom - minZoom)) * 100

  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newZoom = parseFloat(e.target.value)
      void zoomTo(newZoom, { duration: 100 })
    },
    [zoomTo]
  )

  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(zoom + step, maxZoom)
    void zoomTo(newZoom, { duration: 200 })
  }, [zoom, step, maxZoom, zoomTo])

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(zoom - step, minZoom)
    void zoomTo(newZoom, { duration: 200 })
  }, [zoom, step, minZoom, zoomTo])

  const zoomPercentage = Math.round(zoom * 100)

  return (
    <SliderContainer>
      <ZoomButton onClick={handleZoomOut} disabled={zoom <= minZoom} aria-label="Zoom out">
        <Minus size={14} />
      </ZoomButton>

      <SliderTrack>
        <SliderFill $percentage={percentage} />
        <ThresholdMarker $percentage={thresholdPercentage} />
        <SliderInput
          type="range"
          min={minZoom}
          max={maxZoom}
          step={0.01}
          value={zoom}
          onChange={handleSliderChange}
          aria-label="Zoom level"
        />
        <SliderThumb $percentage={percentage} />
      </SliderTrack>

      <ZoomButton onClick={handleZoomIn} disabled={zoom >= maxZoom} aria-label="Zoom in">
        <Plus size={14} />
      </ZoomButton>

      <ZoomLabel>{zoomPercentage}%</ZoomLabel>
    </SliderContainer>
  )
}
